import { saveAs } from "file-saver";

import store from "../globals";
import { axiosInstance } from "../services/v2/axiosInstance";

/**
 * Descargas de toda WAPP (Esteban, 2026-10-09: "cuando se descarga se va a Mis archivos en WappDrive y se limita en
 * sobremanera descargar… porque no hay necesidad"). Todos los botones de "Descargar" pasan por aquí:
 *  - Quien puede sacar archivos de WAPP (Super Admin y las autorizadas en WappDrive › Compartir con externos): baja a su
 *    computadora y queda en la bitácora.
 *  - Todos los demás: el archivo se guarda en Mis archivos › Descargas de WAPP › <módulo> y sale un aviso con "Abrir".
 */

export const campusActual = (): number | null => {
	const s: any = store.getState();
	const legacy = s.currentUserCampus?.value && s.campusRef?.value ? s.campusRef.value[s.currentUserCampus.value] : null;
	return legacy?.id ?? s.rootReducer?.sessionState?.currentCampus?.id ?? null;
};
const base = (c: number) => `/admin/campus/${c}/wappdrive`;

let permisos: Promise<{ descargar: boolean }> | null = null;
/** Una vez por sesión de la pestaña. Si falla, se asume que no puede descargar (lo seguro). */
export const puedeDescargar = async (): Promise<boolean> => {
	const c = campusActual();
	if (!c) return false;
	if (!permisos) permisos = axiosInstance.get(`${base(c)}/_permisos`).then((r) => r.data).catch(() => { permisos = null; return { descargar: false }; });
	return (await permisos).descargar;
};

/** Aviso flotante abajo al centro, con un botón opcional. Sin dependencias: sirve en pantallas viejas y nuevas. */
export const avisoFlotante = (texto: string, accion?: { texto: string; href: string }, tono: "ok" | "error" = "ok"): void => {
	document.querySelectorAll(".wa-aviso-flotante").forEach((e) => e.remove());
	const d = document.createElement("div");
	d.className = "wa-aviso-flotante" + (tono === "error" ? " error" : "");
	d.setAttribute("role", "status");
	const t = document.createElement("span");
	t.textContent = texto;
	d.appendChild(t);
	if (accion) {
		const a = document.createElement("a");
		// En la misma ventana, sin recargar (Esteban: nada de abrir otra pestaña). React Router escucha popstate.
		a.href = accion.href; a.textContent = accion.texto;
		a.onclick = (e) => {
			e.preventDefault(); d.remove();
			// Ya en esa pantalla (WappDrive/WappCell leen el enlace solo al abrir): recarga en la misma ventana.
			if (window.location.pathname === accion.href.split("?")[0]) { window.location.href = accion.href; return; }
			window.history.pushState({}, "", accion.href);
			window.dispatchEvent(new PopStateEvent("popstate"));
		};
		d.appendChild(a);
	}
	const x = document.createElement("button");
	x.type = "button"; x.setAttribute("aria-label", "Cerrar"); x.textContent = "×";
	x.onclick = () => d.remove();
	d.appendChild(x);
	document.body.appendChild(d);
	setTimeout(() => d.remove(), 9000);
};

const RUTA_APP: Record<string, string> = { word: "wappdocs", cell: "wappcell", slide: "wappdeck", pdf: "wapppdf" };

/**
 * Entregar un archivo generado o bajado por WAPP. `origen` es el módulo ("Pagos", "Facturas", "Kits"…): con él se arma la
 * carpeta en WappDrive y la línea de la bitácora.
 */
export const entregarArchivo = async (datos: Blob, nombre: string, origen: string): Promise<void> => {
	const c = campusActual();
	if (c && (await puedeDescargar())) {
		saveAs(datos, nombre);
		axiosInstance.post(`${base(c)}/_descargado`, { nombre, origen }).catch(() => null);
		return;
	}
	if (!c) { avisoFlotante("No se pudo guardar: vuelve a entrar a WAPP.", undefined, "error"); return; }
	avisoFlotante("Guardando en tu WappDrive…");
	try {
		const fd = new FormData();
		fd.append("archivo", datos, nombre);
		fd.append("nombre", nombre);
		fd.append("origen", origen);
		const r = await axiosInstance.post(`${base(c)}/_guardar`, fd);
		const x = r.data as { id: number; carpeta: number; ruta: string; editor: string | null };
		const href = x.editor && RUTA_APP[x.editor] ? `/${RUTA_APP[x.editor]}?d=${x.id}` : `/wappdrive?ver=${x.id}`;
		avisoFlotante(`Guardado en ${x.ruta}`, { texto: "Abrir", href });
	} catch (e: any) {
		avisoFlotante(e?.response?.data?.message || "No se pudo guardar en WappDrive. Intenta otra vez.", undefined, "error");
	}
};

/**
 * Para VER un archivo de oficina (Excel, Word, PowerPoint) que llegó como adjunto (Esteban, 2026-10-12: Christy no podía
 * abrir un .xlsx). Se guarda en Mis archivos › Descargas de WAPP › <módulo>, igual que una descarga, y se devuelve su id
 * para abrirlo en WappCell / WappDocs ahí mismo. Sin plantel (p. ej. WappMail) devuelve null.
 */
export const guardarParaVer = async (datos: Blob, nombre: string, origen: string): Promise<{ id: number; editor: string | null; campus: number } | null> => {
	const c = campusActual();
	if (!c) return null;
	const fd = new FormData();
	fd.append("archivo", datos, nombre);
	fd.append("nombre", nombre);
	fd.append("origen", origen);
	const r = await axiosInstance.post(`${base(c)}/_guardar`, fd);
	return { id: Number(r.data.id), editor: r.data.editor || null, campus: c };
};
